
import { defineComponent, ref, onMounted, onBeforeUnmount } from "vue";
import { detectPlatform, isRunningInNativeApp, isRunningAsPWA, isNativeWebView, openApp, hasDismissedAppBanner, dismissAppBanner, getAppStoreUrl } from "../lib/AppUtils";
import { XMarkIcon } from "@heroicons/vue/24/solid";

export default defineComponent({
  name: "AppInstallBanner",
  components: { XMarkIcon },
  setup() {
    const showBanner = ref(false);
    const isIOS = ref(false);
    const isIOSSafari = ref(false);
    const bannerMessage = ref("Get the app for a better experience");
    const primaryLabel = ref("Use App");

    // Chrome's deferred PWA install prompt — captured from beforeinstallprompt
    let deferredPwaPrompt: any = null;
    const showPwaInstall = ref(false);

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPwaPrompt = e;
      // Only surface the PWA install button if we're showing the banner and
      // it's Android Chrome (not iOS, not already a PWA / native app).
      if (showBanner.value && detectPlatform() === "android") {
        showPwaInstall.value = true;
      }
    };

    onMounted(async () => {
      window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

      const platform = detectPlatform();
      isIOS.value = platform === "ios";

      const userAgent = navigator.userAgent || "";
      const isIOSChrome = platform === "ios" && /CriOS/.test(userAgent);
      isIOSSafari.value = platform === "ios" && /Safari/.test(userAgent) && !/CriOS/.test(userAgent);

      // Native MAUI app or installed PWA — already in-app, never prompt to install
      if (isRunningInNativeApp() || isNativeWebView() || isRunningAsPWA()) {
        showBanner.value = false;
        return;
      }

      // Android: if deep link params are in the URL and the native app is
      // already installed, open it automatically — no tap required.
      // This handles the flow: email → web app → redirect to mobile PWA.
      const hasDeepLinkParams = /redirect(companyid|propertyid|guid|type)/i.test(window.location.search);
      if (platform === "android" && hasDeepLinkParams && "getInstalledRelatedApps" in navigator) {
        try {
          const related = await (navigator as any).getInstalledRelatedApps();
          const nativeInstalled = related.some((app: any) => app.id === "com.mobile.hellospoke");
          if (nativeInstalled) {
            const currentPath = (window.location.pathname + window.location.search + window.location.hash).replace(/^\//, "");
            openApp(currentPath);
            return;
          }
        } catch {
          // API unavailable — fall through to banner
        }
      }

      // iOS Chrome — show banner unless dismissed this session
      if (isIOSChrome) {
        showBanner.value = sessionStorage.getItem("ios_chrome_banner_dismissed") !== "true";
        return;
      }

      // iOS Safari — Smart App Banner is dismissed by Vue Router pushState,
      // so we show our own in-page banner as a reliable fallback.
      if (isIOSSafari.value) {
        showBanner.value = !hasDismissedAppBanner();
        return;
      }

      if (hasDismissedAppBanner()) return;

      // Android Chrome: check whether the native app is already installed.
      // If so, offer "Open App"; if not, offer "Install App" + PWA option.
      if (platform === "android" && "getInstalledRelatedApps" in navigator) {
        try {
          const related = await (navigator as any).getInstalledRelatedApps();
          const nativeInstalled = related.some((app: any) => app.id === "com.mobile.hellospoke");
          if (nativeInstalled) {
            bannerMessage.value = "Open in the HelloSpoke app";
            primaryLabel.value = "Open App";
          } else {
            bannerMessage.value = "Install the app for a better experience";
            primaryLabel.value = "Install App";
          }
        } catch {
          // API not supported or blocked — fall through to default labels
        }
      }

      showBanner.value = true;
    });

    onBeforeUnmount(() => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    });

    const handleBannerClick = () => {
      const userAgent = navigator.userAgent || "";
      const isIOSChrome = isIOS.value && /CriOS/.test(userAgent);

      if (isIOSChrome) {
        window.location.href = getAppStoreUrl();
        return;
      }

      // Pass the full current path so the app opens to the exact deep-linked
      // destination instead of always landing on the login screen.
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      const cleanPath = currentPath.replace(/^\//, "");

      if (isIOSSafari.value) {
        // Universal link with same host so the MAUI app (which only
        // intercepts URLs for its registered associated-domains) opens.
        window.location.href = `https://${window.location.hostname}/${cleanPath}`;
        return;
      }

      openApp(cleanPath);
    };

    const handlePwaInstall = async () => {
      if (!deferredPwaPrompt) return;
      deferredPwaPrompt.prompt();
      const { outcome } = await deferredPwaPrompt.userChoice;
      if (outcome === "accepted") {
        showBanner.value = false;
        showPwaInstall.value = false;
        dismissAppBanner();
      }
      deferredPwaPrompt = null;
    };

    const dismissBanner = () => {
      showBanner.value = false;
      showPwaInstall.value = false;
      const userAgent = navigator.userAgent || "";
      const isIOSChrome = isIOS.value && /CriOS/.test(userAgent);
      if (isIOSChrome) {
        sessionStorage.setItem("ios_chrome_banner_dismissed", "true");
      } else {
        dismissAppBanner();
      }
    };

    return { showBanner, showPwaInstall, bannerMessage, primaryLabel, handleBannerClick, handlePwaInstall, dismissBanner };
  },
});
